import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import { cn } from '@/lib/utils'
import { budgetExhausted, type AuthBudget } from '@/components/account/ConnectionCheckMeter'
import { Replace, Loader2, AlertCircle, ArrowRightLeft, ShieldCheck, AlertTriangle, RotateCw } from 'lucide-react'

interface SwapProperty {
  id: number
  name: string
}

interface SwapTarget {
  id: number
  name: string
  properties: SwapProperty[]
}

// A connection in the same business that these properties could move ONTO. Only
// owned connections qualify (you cannot move hotels onto one you merely borrow).
export interface SwapCandidate {
  id: number
  name: string
  clientIdMasked: string
  environment: string
  healthStatus: 'healthy' | 'degraded' | 'suspended'
  authBudget: AuthBudget | null
}

interface SwapCredentialModalProps {
  /**
   * `/a/:account_slug/settings` — the business's settings root, served by the backend
   * (`currentAccount.settingsPath`). Every endpoint below is this plus a FIXED suffix.
   */
  settingsPath: string
  connection: SwapTarget | null
  /** Other owned connections in this business — the destination list. May be empty. */
  candidates: SwapCandidate[]
  // Whether to offer the Eviivo Environment selector. False on the real production
  // deployment. Presentation only; SwapEviivoCredentialMutation coerces regardless.
  allowStagingEnvironment: boolean
  onClose: () => void
}

type SwapMode = 'existing' | 'new'

const HEALTH_CHIP: Record<SwapCandidate['healthStatus'], { label: string; className: string }> = {
  healthy: { label: 'Connected', className: 'bg-emerald-50 text-emerald-600' },
  degraded: { label: 'Reconnecting', className: 'bg-amber-50 text-amber-600' },
  suspended: { label: 'Needs attention', className: 'bg-rose-50 text-rose-600' },
}

// WS-F5: move this connection's properties onto a different one.
//
// THE PROD INCIDENT THIS FIXES: a hotel was moved between business accounts carrying a
// SUSPENDED credential. The fix the operator needed was to point it at a connection that
// ALREADY EXISTED and ALREADY WORKED in the same business (serving four other hotels) — but
// the only thing this dialog offered was a brand-new Client ID and secret, which nobody has
// memorised. They were blocked and had to be rescued through the admin back-office. The
// "Use a connection you already have" mode below IS that missing path (it posts
// new_credential_id, never alongside the new-credential trio).
//
// The pre-flight promise is unchanged and load-bearing: the server only moves the properties
// if the destination can see EVERY one of them in that PMS account. A refusal means NOTHING
// changed — the copy says so, because a half-moved portfolio is the one outcome we never risk.
export default function SwapCredentialModal({
  settingsPath,
  connection,
  candidates,
  allowStagingEnvironment,
  onClose,
}: SwapCredentialModalProps) {
  const { addToast } = useToast()
  const hasCandidates = candidates.length > 0
  const [mode, setMode] = useState<SwapMode>(hasCandidates ? 'existing' : 'new')
  const [selectedCredentialId, setSelectedCredentialId] = useState<string>(
    hasCandidates ? String(candidates[0].id) : ''
  )
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [environment, setEnvironment] = useState('production')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  React.useEffect(() => {
    setMode(hasCandidates ? 'existing' : 'new')
    setSelectedCredentialId(hasCandidates ? String(candidates[0].id) : '')
    setClientId('')
    setClientSecret('')
    setEnvironment('production')
    setError(null)
    setSaving(false)
    // Re-seeded whenever a different connection opens the dialog (deliberately keyed on the
    // target id alone — the candidate list is stable for the life of one open dialog).
  }, [connection?.id])

  const properties = connection?.properties ?? []
  const selected = candidates.find((candidate) => String(candidate.id) === selectedCredentialId) ?? null
  // The destination spends ITS OWN hourly checks on the pre-flight, so an exhausted
  // destination is what blocks — never the connection being replaced.
  const selectedExhausted = budgetExhausted(selected?.authBudget)
  const canSubmit =
    properties.length > 0 &&
    (mode === 'existing'
      ? selectedCredentialId !== '' && !selectedExhausted
      : clientId.trim() !== '' && clientSecret.trim() !== '')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!connection || !canSubmit) return
    setError(null)
    setSaving(true)

    try {
      // EXACTLY ONE destination shape reaches the server — an existing connection id, or the
      // new-credential trio. Sending both is a documented 422 ("not both").
      const body =
        mode === 'existing'
          ? { new_credential_id: Number(selectedCredentialId) }
          : { client_id: clientId.trim(), client_secret: clientSecret, environment }

      const response = await apiFetch(`${settingsPath}/pms-connections/${connection.id}/swap`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      const data = await response.json()

      if (!response.ok) {
        setError(data.errors?.join(', ') || 'We could not move these properties. Nothing was changed.')
        return
      }

      const moved: SwapProperty[] = data.movedProperties ?? []
      addToast({
        variant: 'success',
        title: 'Properties moved',
        description:
          `${moved.length} ${moved.length === 1 ? 'property is' : 'properties are'} now on ` +
          `${mode === 'existing' && selected ? selected.name : 'the new connection'}.` +
          (data.environmentWarning ? ` ${data.environmentWarning}` : ''),
      })
      onClose()
      router.reload({ only: ['connections'] })
    } catch (e) {
      setError(apiErrorMessage(e, 'Network error. Please check your connection and try again.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={connection !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Move properties</DialogTitle>
          <DialogDescription>
            Point the properties on {connection?.name} at a different PMS connection. They only move if the new
            connection can serve every one of them.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="space-y-4">
            {/* Pre-flight: exactly what will move, named before anything is committed. */}
            <div className="rounded-lg border border-border/60 bg-muted/40 p-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground" />
                {properties.length === 0 ? 'No properties to move' : 'These properties will move'}
              </div>
              {properties.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {properties.map((p) => (
                    <li key={p.id} className="text-xs text-muted-foreground">
                      {p.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Destination mode. Reusing a connection you already have is the default whenever
                one exists — it is the recovery path, and it needs no secret. */}
            {hasCandidates && (
              <div
                role="group"
                aria-label="Where these properties should move"
                className="inline-flex h-10 items-center rounded-lg bg-muted p-1 text-muted-foreground"
              >
                <button
                  type="button"
                  onClick={() => setMode('existing')}
                  aria-pressed={mode === 'existing'}
                  className={cn(
                    'inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    mode === 'existing' ? 'bg-white text-foreground shadow-sm' : 'hover:text-foreground'
                  )}
                >
                  A connection you have
                </button>
                <button
                  type="button"
                  onClick={() => setMode('new')}
                  aria-pressed={mode === 'new'}
                  className={cn(
                    'inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    mode === 'new' ? 'bg-white text-foreground shadow-sm' : 'hover:text-foreground'
                  )}
                >
                  New credentials
                </button>
              </div>
            )}

            {mode === 'existing' ? (
              <div className="space-y-2">
                <Label htmlFor="swap-existing-connection">Move them to</Label>
                <Select value={selectedCredentialId} onValueChange={setSelectedCredentialId}>
                  <SelectTrigger id="swap-existing-connection">
                    <SelectValue placeholder="Choose a connection…" />
                  </SelectTrigger>
                  <SelectContent>
                    {candidates.map((candidate) => {
                      const chip = HEALTH_CHIP[candidate.healthStatus] ?? HEALTH_CHIP.healthy
                      return (
                        <SelectItem key={candidate.id} value={String(candidate.id)}>
                          <span className="flex items-center gap-2">
                            <span className="font-medium">{candidate.name}</span>
                            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                              {candidate.clientIdMasked}
                            </span>
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                                chip.className
                              )}
                            >
                              {chip.label}
                            </span>
                          </span>
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>

                {/* Honest warnings about the chosen destination, before the click. */}
                {selected && selected.healthStatus !== 'healthy' && (
                  <p className="flex items-start gap-1.5 text-xs text-amber-600">
                    {selected.healthStatus === 'suspended' ? (
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    ) : (
                      <RotateCw className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    )}
                    <span>
                      That connection isn&apos;t healthy either right now. Moving onto it may not restore pricing.
                    </span>
                  </p>
                )}
                {selected && selected.healthStatus === 'healthy' && (
                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                    <span>This connection is working, and you won&apos;t need to enter a secret.</span>
                  </p>
                )}
                {selectedExhausted && (
                  <p role="alert" className="flex items-start gap-1.5 text-xs text-amber-600">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      That connection has no checks left this hour, so we can&apos;t verify it yet. Pick another, or try
                      again shortly.
                    </span>
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="swap-client-id">New Client ID</Label>
                  <Input
                    id="swap-client-id"
                    type="text"
                    autoComplete="off"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="swap-client-secret">New Client Secret</Label>
                  <Input
                    id="swap-client-secret"
                    type="password"
                    autoComplete="off"
                    placeholder="Enter the client secret"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>

                {/* Eviivo Environment. Hidden on the real production deployment: every hotelier's
                    Eviivo account is production, and a wrong pick authenticates against the QA host
                    — three failures suspend a live connection. The backend coerces regardless. */}
                {allowStagingEnvironment && (
                  <div className="space-y-2">
                    <Label htmlFor="swap-environment">Environment</Label>
                    <Select value={environment} onValueChange={setEnvironment}>
                      <SelectTrigger id="swap-environment">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="production">Production</SelectItem>
                        <SelectItem value="staging">Staging</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              {mode === 'existing'
                ? 'We check the chosen connection can see every property listed above. If even one is missing, nothing moves and we name it. The old connection is kept, just emptied.'
                : 'Verifying uses one of the connection checks your PMS allows each hour. If the new account is missing even one property, nothing moves. The old connection is kept, just emptied.'}
            </p>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" className="rounded-lg" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" className="rounded-lg" disabled={saving || !canSubmit} aria-busy={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Verifying…
                </>
              ) : (
                <>
                  <Replace className="mr-1.5 h-3.5 w-3.5" />
                  Verify &amp; move
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
